import React, { useState } from 'react';
import PatientTimeline from './PatientTimeline';
import PatientAiSummary from './PatientAiSummary';
import TagSelector from './TagSelector';
import AppointmentDetailPanel from './AppointmentDetailPanel';
import api from '../services/api';
import PaymentMethodsPanel from './PaymentMethodsPanel';

function PatientDetailPanel({ patient, practiceId, onClose, onPatientUpdated }) {
  const [activeTab, setActiveTab] = useState('timeline');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  if (!patient) return null;

  const tabs = [
    { key: 'timeline', label: 'Timeline' },
    { key: 'appointments', label: 'Appointments' },
    { key: 'summary', label: 'AI Summary' },
    { key: 'info', label: 'Details' },
    { key: 'payments', label: 'Payments' }
  ];

  return (
    <div style={styles.panel}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.avatar}>
            {(patient.first_name || '?')[0]}{(patient.last_name || '?')[0]}
          </div>
          <div>
            <div style={styles.name}>{patient.first_name} {patient.last_name}</div>
            <div style={styles.meta}>
              {patient.phone && <span>{patient.phone}</span>}
              {patient.email && <span> &middot; {patient.email}</span>}
            </div>
            {practiceId && (
              <div style={{ marginTop: 6 }}>
                <TagSelector
                  practiceId={practiceId}
                  entityType="patient"
                  entityId={patient.id}
                  canManage={true}
                />
              </div>
            )}
          </div>
        </div>
        <button onClick={onClose} style={styles.closeBtn}>&times;</button>
      </div>

      {/* Tabs — Prominent Pill Style */}
      <div style={styles.tabRow}>
        <div style={styles.tabWrapper}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                ...styles.tab,
                ...(activeTab === tab.key ? styles.tabActive : {})
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {activeTab === 'timeline' && (
          <PatientTimeline practiceId={practiceId} patientId={patient.id} />
        )}
        {activeTab === 'appointments' && (
          <PatientAppointmentsTab practiceId={practiceId} patientId={patient.id} />
        )}
        {activeTab === 'summary' && (
          <PatientAiSummary practiceId={practiceId} patientId={patient.id} context="detail" />
        )}
        {activeTab === 'info' && (
          editing ? (
            <PatientEditForm
              patient={patient}
              editData={editData}
              setEditData={setEditData}
              saving={saving}
              onSave={async () => {
                setSaving(true);
                try {
                  // GH-42: backend now also pushes the change to athena via Bridge.
                  // If the practice doesn't have Athena Go-Live approval, the local
                  // save still succeeds and the response includes athena_warning so
                  // the staff member knows the change is local-only for now.
                  const res = await api.updatePatient(patient.id, editData);
                  setEditing(false);
                  // Hand the saved record back so the panel re-renders with the
                  // new values. Without it the parent refreshed only its list
                  // and this panel kept showing the pre-edit patient, which
                  // reads as "the save did nothing" (prod, 2026-10-24: a DOB
                  // correction was submitted twice because of it).
                  if (onPatientUpdated) onPatientUpdated(res?.data);
                  if (res?.athena_warning) {
                    alert('Saved locally. ' + res.athena_warning);
                  } else {
                    alert('Patient updated successfully.');
                  }
                } catch (err) {
                  alert('Error saving: ' + err.message);
                } finally {
                  setSaving(false);
                }
              }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <button onClick={() => { setEditing(true); setEditData({ first_name: patient.first_name || '', last_name: patient.last_name || '', phone: patient.phone || '', email: patient.email || '', date_of_birth: patient.date_of_birth || '' }); }} style={styles.editBtn}>Edit Patient</button>
              </div>
              <div style={styles.detailGrid}>
                <DetailRow label="Name" value={`${patient.first_name} ${patient.last_name}`} />
                <DetailRow label="Phone" value={patient.phone} />
                <DetailRow label="Email" value={patient.email} />
                <DetailRow label="DOB" value={patient.date_of_birth} />
                <DetailRow label="Last Visit" value={patient.last_visit_date} />
                <DetailRow label="Status" value={patient.opted_out ? 'Opted Out' : 'Active'} />
                {patient.athena_patient_id && (
                  <DetailRow label="Athena ID" value={patient.athena_patient_id} />
                )}
              </div>
            </div>
          )
        )}
        {activeTab === 'payments' && (
          <PaymentMethodsPanel patientId={patient.id} practiceId={practiceId} />
        )}
      </div>
    </div>
  );
}

function PatientEditForm({ patient, editData, setEditData, saving, onSave, onCancel }) {
  const fields = [
    { key: 'first_name', label: 'First Name', type: 'text' },
    { key: 'last_name', label: 'Last Name', type: 'text' },
    { key: 'phone', label: 'Phone', type: 'tel' },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
  ];
  return (
    <div style={styles.detailGrid}>
      {fields.map(f => (
        <div key={f.key} style={styles.detailRow}>
          <span style={styles.detailLabel}>{f.label}</span>
          <input
            type={f.type}
            value={editData[f.key] || ''}
            onChange={e => setEditData({ ...editData, [f.key]: e.target.value })}
            style={{ flex: 1, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
          />
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button onClick={onSave} disabled={saving} style={styles.saveBtn}>{saving ? 'Saving...' : 'Save'}</button>
        <button onClick={onCancel} style={styles.cancelBtn}>Cancel</button>
      </div>
    </div>
  );
}

// Past + present appointments list. Click a row to open the appointment-detail
// panel inline (mirrors the pattern used on the Appointments page).
function PatientAppointmentsTab({ practiceId, patientId }) {
  const [appointments, setAppointments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selectedAppt, setSelectedAppt] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getPatientAppointments(practiceId, patientId)
      .then(res => { if (!cancelled) setAppointments(res?.data || []); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [practiceId, patientId]);

  if (selectedAppt) {
    return (
      <AppointmentDetailPanel
        appointment={selectedAppt}
        practiceId={practiceId}
        onClose={() => setSelectedAppt(null)}
      />
    );
  }

  if (loading) return <p style={{ color: '#9ca3af', textAlign: 'center', padding: 24 }}>Loading appointments…</p>;
  if (error) return <p style={{ color: '#dc2626', textAlign: 'center', padding: 24 }}>Failed to load: {error}</p>;
  if (appointments.length === 0) return <p style={{ color: '#9ca3af', textAlign: 'center', padding: 24 }}>No appointments on file for this patient.</p>;

  const todayIso = new Date().toISOString().slice(0, 10);
  const upcoming = appointments.filter(a => (a.appointment_date || '').slice(0, 10) >= todayIso);
  const past = appointments.filter(a => (a.appointment_date || '').slice(0, 10) < todayIso);

  return (
    <div>
      {upcoming.length > 0 && (
        <Section title="Upcoming">
          {upcoming.map(a => <AppointmentRow key={a.id} appt={a} onClick={() => setSelectedAppt(a)} />)}
        </Section>
      )}
      {past.length > 0 && (
        <Section title="Past">
          {past.map(a => <AppointmentRow key={a.id} appt={a} onClick={() => setSelectedAppt(a)} />)}
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, padding: '8px 0 6px' }}>
        {title}
      </div>
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
}

// Parse the date-portion of an ISO timestamp directly (no Date constructor)
// to avoid timezone shift — `new Date('2026-05-02T00:00:00Z').toLocaleDateString`
// renders as "May 1" anywhere west of UTC. Mirrors AppointmentDetailPanel.js
// formatApptDate. (commons follow-up to GH-2 / patient-appts-tab work.)
function formatApptDate(dateStr) {
  if (!dateStr) return '—';
  const parts = String(dateStr).split('T')[0].split('-').map(Number);
  if (parts.length === 3 && parts[0] > 1900) {
    const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 12, 0, 0));
    return d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
  }
  return dateStr;
}

// Render "9:30 AM" instead of the raw "09:30" / "09:30:00" stored on the row.
function formatStartTime(t) {
  if (!t) return '';
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return String(t);
  const h = parseInt(m[1], 10);
  const min = m[2];
  const period = h >= 12 ? 'PM' : 'AM';
  const display = h % 12 || 12;
  return `${display}:${min} ${period}`;
}

function AppointmentRow({ appt, onClick }) {
  const statusColors = {
    scheduled: { bg: '#dbeafe', text: '#1e40af' },
    completed: { bg: '#dcfce7', text: '#166534' },
    'no-show': { bg: '#fef3c7', text: '#92400e' },
    'checked-in': { bg: '#cffafe', text: '#155e75' },
    canceled: { bg: '#fee2e2', text: '#991b1b' },
    rescheduled: { bg: '#e0e7ff', text: '#3730a3' },
  };
  const sc = statusColors[appt.status] || { bg: '#f3f4f6', text: '#6b7280' };
  const date = formatApptDate(appt.appointment_date);
  const time = formatStartTime(appt.start_time);
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
        borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
          {date}{time ? ` · ${time}` : ''}
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
          {appt.appointment_type || 'Appointment'}
          {appt.provider_name ? ` · ${appt.provider_name}` : ''}
        </div>
      </div>
      <div style={{
        padding: '4px 10px', borderRadius: 999, background: sc.bg, color: sc.text,
        fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        {appt.status || 'unknown'}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={styles.detailRow}>
      <span style={styles.detailLabel}>{label}</span>
      <span style={styles.detailValue}>{value || '—'}</span>
    </div>
  );
}

const styles = {
  panel: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: '#f8fafc',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '24px 28px', borderBottom: '1px solid #e2e8f0', flexShrink: 0,
    background: '#ffffff',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
  avatar: {
    width: '52px', height: '52px', borderRadius: '12px',
    background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '18px', fontWeight: '700',
    fontFamily: "'JetBrains Mono', monospace",
    boxShadow: '0 4px 14px rgba(37,99,235,0.2)',
  },
  name: {
    fontSize: '22px', fontWeight: '700', color: '#0f172a',
    fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.5px',
  },
  meta: { fontSize: '13px', color: '#94a3b8', marginTop: '4px' },
  closeBtn: {
    background: 'none', border: '1px solid #e2e8f0', fontSize: '18px', color: '#94a3b8',
    cursor: 'pointer', padding: '6px 10px', lineHeight: 1,
    borderRadius: '8px', transition: 'all 0.2s',
  },
  tabRow: {
    display: 'flex', gap: '4px', padding: '12px 24px',
    flexShrink: 0, background: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
  },
  tabWrapper: {
    display: 'flex', gap: '4px', flex: 1,
    background: '#f1f5f9', borderRadius: '14px', padding: '5px',
  },
  tab: {
    flex: 1, padding: '11px 8px', background: 'transparent', border: 'none',
    fontSize: '13px', fontWeight: '600', color: '#94a3b8', cursor: 'pointer',
    borderRadius: '10px', transition: 'all 0.25s ease', textAlign: 'center',
  },
  tabActive: {
    color: '#ffffff', background: '#2563eb',
    boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
  },
  content: {
    flex: 1, overflowY: 'auto', padding: '24px 28px'
  },
  detailGrid: { display: 'flex', flexDirection: 'column', gap: '0',
    background: '#ffffff', borderRadius: '14px',
    border: '1px solid #e2e8f0', overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
  },
  detailRow: { display: 'flex', gap: '16px', padding: '14px 20px', borderBottom: '1px solid #e2e8f0', alignItems: 'center' },
  detailLabel: {
    width: '120px', fontSize: '10px', color: '#94a3b8', fontWeight: '600', flexShrink: 0,
    textTransform: 'uppercase', letterSpacing: '1.2px',
  },
  detailValue: { fontSize: '14px', color: '#0f172a', fontWeight: '600' },
  editBtn: {
    padding: '10px 24px', fontSize: '13px', fontWeight: '600', color: '#2563eb',
    background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 10, cursor: 'pointer',
    transition: 'all 0.2s',
  },
  saveBtn: {
    padding: '8px 20px', fontSize: '13px', fontWeight: '600', color: '#fff',
    background: '#2563eb', border: 'none', borderRadius: 8, cursor: 'pointer',
  },
  cancelBtn: {
    padding: '8px 20px', fontSize: '13px', fontWeight: '600', color: '#334155',
    background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer',
  }
};

export default PatientDetailPanel;
